/**
 * Library -> Clips: what plays between songs, and when. Core: Flow, playlists,
 * Auto DJ and any plugin use these rules (each playlist and flow has its own
 * "Clip rules" switch, off by default).
 *
 * Each rule reads as a sentence ("Sweeper, any time, every 3 songs"). Rules are
 * independent: each plays as often as it says. Clips due at the same break all
 * play, in the clip-type order of Settings -> Clips, then in the order of this
 * list (drag to change it). A combo is a run of clips played as one. A clip
 * type or one clip can also play over a song (its intro, its ending, or a point
 * in it) while the song dips; a combo plays between songs.
 *
 * "When" is "any time", or a kind a plugin adds while it is on (Radio: between
 * shows, during, before or after a show); the engine lists them in ``kinds``.
 * The preview simulates a day with the same planner that plays them.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { engineBase } from "../api";
import { useApp } from "../core/app";
import { moved, useDragReorder } from "../core/dragList";
import { Icon } from "../shell/Icons";
import { toast } from "../shell/Toast";
import { ClipTypesSettings } from "./ClipTypesSettings";
import "./clips.css";

type WhatKind = "type" | "clip" | "combo";
type Placement = "between" | "intro" | "outro" | "at";
type OftenKind = "songs" | "hour" | "top" | "day" | "ramp";

interface Often {
  kind: OftenKind;
  n: number | null;
  days: number | null;
  first: number | null;
  last: number | null;
  byDay: Record<number, number>;
}

interface Rule {
  id?: number;
  enabled: boolean;
  what: { kind: WhatKind; ref: string };
  when: { kind: string; ref: string | null };
  often: Often;
  placement: Placement;
  placementSeconds: number | null;
  hours: { from: string; to: string } | null;
}

interface Combo {
  id?: number;
  name: string;
  items: { kind: "type" | "clip"; ref: string }[];
}

interface Clip {
  trackId: string;
  title: string | null;
  type: string;
  duration: number | null;
}

/** A kind of "when": "any time", or one a plugin adds. */
interface WhenKind {
  kind: string;
  label: string;
  hint?: string;
  /** What it names (Radio: a show), with the choices. */
  ref?: { label: string; options: { id: string; name: string }[] };
  /** "How often" is a ramp over the days before or after. */
  ramp?: boolean;
  owner?: string;
}

interface Page {
  rules: Rule[];
  combos: Combo[];
  types: { id: string; name: string }[];
  kinds: WhenKind[];
  clips: Clip[];
  error?: string;
}

interface Preview {
  date: string;
  songs: number;
  breaks: { at: string; show: string | null; hits: { ruleId: number; placement?: Placement; clips: (Clip & { typeName: string })[] }[] }[];
  totals: Record<string, number>;
  error?: string;
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${engineBase()}/api/clips/${path}`, body === undefined ? undefined : {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "before" or "after" a ramp counts days towards or away from. */
const rampAfter = (kind: string) => kind === "after";

function whenText(rule: Rule, page: Page): string {
  const k = page.kinds.find((x) => x.kind === rule.when.kind);
  if (!k) return `“${rule.when.kind}” (needs a plugin that is off)`;
  if (!k.ref) return k.label.toLowerCase();
  const name = k.ref.options.find((o) => o.id === String(rule.when.ref))?.name ?? `a deleted ${k.ref.label.toLowerCase()}`;
  return `${rule.when.kind} ${name}`;
}

/** "Sweeper, any time, every 3 songs". */
function sentence(rule: Rule, page: Page): { what: string; when: string; often: string } {
  const typeName = (id: string) => page.types.find((t) => t.id === id)?.name ?? id;
  const what =
    rule.what.kind === "type" ? `Any ${typeName(rule.what.ref)}`
    : rule.what.kind === "clip" ? `“${page.clips.find((c) => c.trackId === rule.what.ref)?.title ?? `file ${rule.what.ref}`}”`
    : `Combo “${page.combos.find((c) => String(c.id) === rule.what.ref)?.name ?? "?"}”`;
  const o = rule.often;
  const named = !!page.kinds.find((x) => x.kind === rule.when.kind)?.ref;
  const often =
    o.kind === "songs" ? `every ${plural(o.n ?? 0, "song")}`
    : o.kind === "hour" ? `${plural(o.n ?? 0, "time")} an hour`
    : o.kind === "top" ? "at the top of every hour"
    : o.kind === "day" ? `${plural(o.n ?? 0, "time")} ${named ? "each time it is on" : "a day"}`
    : !rampAfter(rule.when.kind)
      ? `from ${plural(o.days ?? 0, "day")} before: ${o.first} a day, changing to ${o.last} on the day`
      : `for ${plural(o.days ?? 0, "day")} after: ${o.first} a day, changing to ${o.last} by the last day`;
  const hours = rule.hours ? `${often}, only ${rule.hours.from}–${rule.hours.to}` : often;
  return { what, when: whenText(rule, page), often: `${hours}${placementText(rule)}` };
}

function placementText(rule: Pick<Rule, "placement" | "placementSeconds">): string {
  if (rule.placement === "intro") return ", over the song's intro";
  if (rule.placement === "outro") return ", over the song's ending";
  if (rule.placement === "at") return `, over the song ${rule.placementSeconds ?? 0} s in`;
  return "";
}

/** Plays on day k of a ramp, as the engine counts them. */
function rampPlays(o: Often, k: number, after: boolean): number {
  if (o.byDay[k] !== undefined) return o.byDay[k];
  const days = o.days || 1;
  const step = (after ? k : days - k) / days;
  return Math.round((o.first ?? 0) + ((o.last ?? 0) - (o.first ?? 0)) * step);
}

const newRule = (page: Page): Rule => ({
  enabled: true,
  what: { kind: "type", ref: page.types[0]?.id ?? "" },
  when: { kind: "any", ref: null },
  often: { kind: "top", n: null, days: null, first: null, last: null, byDay: {} },
  placement: "between",
  placementSeconds: null,
  hours: null,
});

export function ClipsView() {
  const app = useApp();
  const [page, setPage] = useState<Page | null>(null);
  const [rule, setRule] = useState<Rule | null>(null);
  const [combo, setCombo] = useState<Combo | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Rules, or the clip types they choose from (also in Settings -> Clips). */
  const [tab, setTab] = useState<"rules" | "types">("rules");

  const load = useCallback(() => void call<Page>("rules").then(setPage).catch((e) => setError(String(e))), []);
  useEffect(() => {
    if (!app.offline) load();
  }, [app.offline, load]);

  const run = async (path: string, body: unknown, done?: string) => {
    const r = await call<Page>(path, body);
    if (r.error) {
      toast(r.error, "bad");
      return false;
    }
    setPage(r);
    if (done) toast(done, "ok");
    return true;
  };

  const drag = useDragReorder({
    onMove: (from, to) => {
      if (!page) return;
      const next = moved(page.rules, from, to);
      setPage({ ...page, rules: next });
      void run("rules/order", { ids: next.map((r) => r.id) });
    },
  });

  if (app.offline) return <section className="nv-view"><div className="nv-empty">Start the engine to see the clip rules.</div></section>;
  if (!page) return <section className="nv-view"><div className="nv-empty">{error ?? "Reading…"}</div></section>;

  return (
    <section className="nv-view cl-rules" aria-label="Clips">
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>Clips</h1>
          <div className="nv-sub">
            {plural(page.clips.length, "clip")} · {plural(page.rules.length, "rule")} · {plural(page.combos.length, "combo")} · a playlist or flow plays them when its Clip rules switch is on
          </div>
        </div>
        <div className="nv-actions">
          {tab === "rules" && <>
          <button className="nv-btn" onClick={() => setCombo({ name: "", items: [{ kind: "type", ref: page.types[0]?.id ?? "" }] })}><Icon name="layers" /> New combo</button>
          <button className="nv-btn primary" onClick={() => setRule(newRule(page))}><Icon name="plus" /> New rule</button>
          </>}
        </div>
      </div>

      <div className="nv-tabs cl-tabs" role="tablist" aria-label="Clips">
        <button role="tab" aria-selected={tab === "rules"} className={tab === "rules" ? "on" : ""} onClick={() => setTab("rules")}>Rules</button>
        <button role="tab" aria-selected={tab === "types"} className={tab === "types" ? "on" : ""} onClick={() => setTab("types")}>Clip types</button>
      </div>

      {tab === "types" ? (
        <div className="cl-types-pane">
          <ClipTypesSettings />
        </div>
      ) : (
      <div className="cl-rules-body">
        <div className="cl-rules-main">
          {page.rules.length === 0 && (
            <div className="nv-empty">
              <Icon name="clock" size={22} />
              <b>No rules yet</b>
              <span>A rule says what plays between songs and when: “Any Station ID, any time, at the top of every hour”.</span>
            </div>
          )}
          <ol className="cl-rulelist">
            {page.rules.map((r, i) => {
              const s = sentence(r, page);
              const rp = drag.rowProps(i);
              return (
                <li key={r.id} {...rp} className={`cl-rule ${r.enabled ? "" : "off"} ${rp.className}`}>
                  <span className="nv-grip" title="Drag to reorder (decides the order when two rules of one type are due together)"><Icon name="grip" size={14} /></span>
                  <label className="nv-toggle" title={r.enabled ? "On: turn off" : "Off: turn on"}>
                    <input type="checkbox" role="switch" checked={r.enabled} onChange={(e) => void run("rules/enable", { id: r.id, enabled: e.target.checked })} />
                    <i aria-hidden="true" />
                  </label>
                  <button className="cl-rule-text" onClick={() => setRule(r)}>
                    <b>{s.what}</b>, {s.when}, {s.often}
                  </button>
                  <button className="nv-iconbtn" title="Delete this rule" onClick={() => window.confirm("Delete this rule?") && void run("rules/delete", { id: r.id }, "Rule deleted.")}><Icon name="trash" size={14} /></button>
                </li>
              );
            })}
          </ol>

          <section className="cl-section">
            <h3>Combos</h3>
            <p className="nv-dim">A run of clips played as one. Each item is a clip type (the next one in rotation) or one particular clip.</p>
            {page.combos.length === 0 && <p className="nv-dim">No combos yet.</p>}
            <ul className="cl-combos">
              {page.combos.map((c) => (
                <li key={c.id}>
                  <button className="cl-rule-text" onClick={() => setCombo(c)}>
                    <b>{c.name}</b>: {c.items.map((it) => (it.kind === "type" ? page.types.find((t) => t.id === it.ref)?.name ?? it.ref : `“${page.clips.find((x) => x.trackId === it.ref)?.title ?? it.ref}”`)).join(" → ")}
                  </button>
                  <button className="nv-iconbtn" title="Delete this combo" onClick={() => window.confirm(`Delete the combo “${c.name}”?`) && void run("combos/delete", { id: c.id }, "Combo deleted.")}><Icon name="trash" size={14} /></button>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <PreviewPane page={page} />
      </div>
      )}

      {rule && <RuleEditor page={page} initial={rule} onClose={() => setRule(null)} onSaved={(p) => { setPage(p); setRule(null); }} />}
      {combo && <ComboEditor page={page} initial={combo} onClose={() => setCombo(null)} onSaved={(p) => { setPage(p); setCombo(null); }} />}
    </section>
  );
}

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="nv-modal" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nv-dialog nv-box wide cl-editor-box" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="nv-iconbtn" title="Close (Esc)" onClick={onClose}><Icon name="close" /></button>
        </header>
        <div className="nv-box-body">{children}</div>
        <footer className="nv-box-foot">{footer}</footer>
      </div>
    </div>
  );
}

function ClipPicker({ page, value, onChange, label }: { page: Page; value: string; onChange: (v: string) => void; label: string }) {
  const [filter, setFilter] = useState("");
  const shown = page.clips.filter((c) => !filter || (c.title ?? "").toLowerCase().includes(filter.toLowerCase())).slice(0, 300);
  const typeName = (id: string) => page.types.find((t) => t.id === id)?.name ?? id;
  return (
    <div className="cl-clippick">
      <input className="nv-input" type="search" placeholder="Find a clip" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label={`Find ${label}`} />
      <select className="nv-input" size={6} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {shown.map((c) => <option key={c.trackId} value={c.trackId}>{c.title} · {typeName(c.type)}</option>)}
      </select>
      {page.clips.length === 0 && <p className="nv-dim">No clips found. Clip types and their words are under the Clip types tab.</p>}
    </div>
  );
}

function RuleEditor({ page, initial, onClose, onSaved }: { page: Page; initial: Rule; onClose: () => void; onSaved: (p: Page) => void }) {
  const [r, setR] = useState<Rule>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const kind = page.kinds.find((k) => k.kind === r.when.kind);
  const ramp = !!kind?.ramp;
  const after = rampAfter(r.when.kind);

  const setWhen = (next: string) => {
    const k = page.kinds.find((x) => x.kind === next);
    const often: Often = k?.ramp
      ? { kind: "ramp", n: null, days: r.often.days ?? 3, first: r.often.first ?? 1, last: r.often.last ?? 4, byDay: {} }
      : r.often.kind === "ramp" ? { kind: "top", n: null, days: null, first: null, last: null, byDay: {} } : r.often;
    const keep = k?.ref?.options.some((o) => o.id === String(r.when.ref));
    setR({ ...r, when: { kind: next, ref: k?.ref ? (keep ? String(r.when.ref) : k.ref.options[0]?.id ?? null) : null }, often });
  };
  const setOften = (patch: Partial<Often>) => setR({ ...r, often: { ...r.often, ...patch } });
  const setWhat = (what: WhatKind) =>
    setR({ ...r, placement: what === "combo" ? "between" : r.placement, placementSeconds: what === "combo" ? null : r.placementSeconds, what: { kind: what, ref: what === "type" ? page.types[0]?.id ?? "" : what === "combo" ? String(page.combos[0]?.id ?? "") : page.clips[0]?.trackId ?? "" } });

  const save = async () => {
    setBusy(true);
    try {
      const out = await call<Page & { error?: string }>("rules/save", r);
      if (out.error) setError(out.error);
      else {
        toast(r.id ? "Rule saved." : "Rule added.", "ok");
        onSaved(out);
      }
    } finally {
      setBusy(false);
    }
  };

  const s = sentence(r, page);
  const days = r.often.days ?? 0;
  const rampDays = Array.from({ length: days + 1 }, (_, i) => (after ? i : days - i));

  return (
    <Modal
      title={r.id ? "Edit rule" : "New rule"}
      onClose={onClose}
      footer={<>
        <span className="cl-sentence"><b>{s.what}</b>, {s.when}, {s.often}.</span>
        <span className="nv-spacer" />
        <button className="nv-btn" onClick={onClose}>Cancel</button>
        <button className="nv-btn primary" disabled={busy} onClick={() => void save()}>{r.id ? "Save" : "Add rule"}</button>
      </>}
    >
      <div className="cl-form">
        <fieldset>
          <legend>What plays</legend>
          <div className="nv-seg" role="radiogroup" aria-label="What plays">
            {([["type", "A clip type"], ["clip", "One clip"], ["combo", "A combo"]] as const).map(([k, label]) => (
              <button key={k} role="radio" aria-checked={r.what.kind === k} className={r.what.kind === k ? "on" : ""} onClick={() => setWhat(k)} disabled={k === "combo" && !page.combos.length}>{label}</button>
            ))}
          </div>
          {r.what.kind === "type" && (
            <select className="nv-input" value={r.what.ref} onChange={(e) => setR({ ...r, what: { kind: "type", ref: e.target.value } })} aria-label="Clip type">
              {page.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {r.what.kind === "type" && <p className="nv-dim">The {page.types.find((t) => t.id === r.what.ref)?.name ?? "clip"} played longest ago goes next.</p>}
          {r.what.kind === "clip" && <ClipPicker page={page} label="Clip" value={r.what.ref} onChange={(v) => setR({ ...r, what: { kind: "clip", ref: v } })} />}
          {r.what.kind === "combo" && (
            <select className="nv-input" value={r.what.ref} onChange={(e) => setR({ ...r, what: { kind: "combo", ref: e.target.value } })} aria-label="Combo">
              {page.combos.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          )}
        </fieldset>

        <fieldset>
          <legend>When</legend>
          <div className="cl-row">
            <select className="nv-input" value={r.when.kind} onChange={(e) => setWhen(e.target.value)} aria-label="When">
              {page.kinds.map((k) => <option key={k.kind} value={k.kind} disabled={!!k.ref && !k.ref.options.length}>{k.label}</option>)}
              {!kind && <option value={r.when.kind} disabled>“{r.when.kind}” (needs a plugin that is off)</option>}
            </select>
            {kind?.ref && (
              <select className="nv-input" value={r.when.ref ?? ""} onChange={(e) => setR({ ...r, when: { ...r.when, ref: e.target.value } })} aria-label={kind.ref.label}>
                {kind.ref.options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
          </div>
          {kind?.hint && <p className="nv-dim">{kind.hint}</p>}
        </fieldset>

        <fieldset>
          <legend>How often</legend>
          {!ramp ? (
            <div className="cl-row">
              <select className="nv-input" value={r.often.kind} onChange={(e) => setOften({ kind: e.target.value as OftenKind, n: e.target.value === "top" ? null : r.often.n ?? 3 })} aria-label="How often">
                <option value="songs">Every N songs</option>
                <option value="hour">N times an hour</option>
                <option value="top">At the top of every hour</option>
                <option value="day">{kind?.ref ? "N times each time it is on" : "N times a day"}</option>
              </select>
              {r.often.kind !== "top" && (
                <input className="nv-input cl-num" type="number" min={1} max={r.often.kind === "songs" ? 50 : r.often.kind === "hour" ? 60 : 500} value={r.often.n ?? ""} onChange={(e) => setOften({ n: e.target.value === "" ? null : Number(e.target.value) })} aria-label="N" />
              )}
            </div>
          ) : (
            <>
              <div className="cl-row cl-ramp">
                <label className="nv-field"><span>{after ? "Carry on this many days after" : "Start this many days before"}</span>
                  <input className="nv-input cl-num" type="number" min={1} max={14} value={r.often.days ?? ""} onChange={(e) => setOften({ days: Number(e.target.value), byDay: {} })} /></label>
                <label className="nv-field"><span>Plays on the first day</span>
                  <input className="nv-input cl-num" type="number" min={0} max={200} value={r.often.first ?? ""} onChange={(e) => setOften({ first: Number(e.target.value) })} /></label>
                <label className="nv-field"><span>{after ? "Plays on the last day" : "Plays on the day"}</span>
                  <input className="nv-input cl-num" type="number" min={0} max={200} value={r.often.last ?? ""} onChange={(e) => setOften({ last: Number(e.target.value) })} /></label>
              </div>
              {days > 0 && days <= 14 && (
                <table className="nv-table cl-ramptable">
                  <thead><tr><th>Day</th><th>Plays</th><th /></tr></thead>
                  <tbody>
                    {rampDays.map((k) => {
                      const own = r.often.byDay[k] !== undefined;
                      return (
                        <tr key={k}>
                          <td>{k === 0 ? (after ? "The day, after it ends" : "The day, until it starts") : `${plural(k, "day")} ${after ? "after" : "before"}`}</td>
                          <td><input className="nv-input cl-num" type="number" min={0} max={200} value={rampPlays(r.often, k, after)}
                            onChange={(e) => setOften({ byDay: { ...r.often.byDay, [k]: Number(e.target.value) } })} aria-label={`Plays ${k} days away`} /></td>
                          <td>{own && <button className="nv-link" onClick={() => { const next = { ...r.often.byDay }; delete next[k]; setOften({ byDay: next }); }}>back to the ramp</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
          <p className="nv-dim">
            {r.often.kind === "top" ? "At the first break in the first quarter of each hour." : r.often.kind === "songs" ? "Counts songs since this rule last played." : "Spread evenly: the first at the first break, then one every interval. Plays missed while Nova was closed are not crammed in later."}
          </p>
        </fieldset>

        <fieldset>
          <legend>Hours of the day</legend>
          <label className="cl-check">
            <input type="checkbox" checked={!!r.hours} onChange={(e) => setR({ ...r, hours: e.target.checked ? { from: "06:00", to: "22:00" } : null })} />
            Only play this between certain hours
          </label>
          {r.hours && (
            <div className="cl-row cl-hours">
              <label className="nv-field"><span>From</span><input className="nv-input" type="time" value={r.hours.from} onChange={(e) => setR({ ...r, hours: { ...r.hours!, from: e.target.value } })} /></label>
              <label className="nv-field"><span>Until</span><input className="nv-input" type="time" value={r.hours.to} onChange={(e) => setR({ ...r, hours: { ...r.hours!, to: e.target.value } })} /></label>
            </div>
          )}
          <p className="nv-dim">{r.hours ? "Plays only inside these hours; plays a day are spread over them." : "Plays at any hour."}</p>
        </fieldset>

        <fieldset>
          <legend>How</legend>
          <div className="nv-seg" role="radiogroup" aria-label="How">
            {([["between", "Between songs"], ["intro", "Over the intro"], ["outro", "Over the ending"], ["at", "At a point"]] as const).map(([k, label]) => (
              <button key={k} role="radio" aria-checked={r.placement === k} className={r.placement === k ? "on" : ""}
                disabled={k !== "between" && r.what.kind === "combo"}
                title={k !== "between" && r.what.kind === "combo" ? "A combo plays between songs" : undefined}
                onClick={() => setR({ ...r, placement: k, placementSeconds: k === "at" ? r.placementSeconds ?? 30 : null })}>{label}</button>
            ))}
          </div>
          {r.placement === "at" && (
            <label className="cl-row">starts <input className="nv-input cl-num" type="number" min={0} max={600} step={1} value={r.placementSeconds ?? 30}
              onChange={(e) => setR({ ...r, placementSeconds: Number(e.target.value) })} aria-label="Seconds into the song" /> seconds into the song</label>
          )}
          <p className="nv-dim">{r.placement === "between" ? "Plays on its own, between two songs."
            : r.placement === "intro" ? "Plays over the next song whose intro is long enough; the song dips under it."
            : r.placement === "outro" ? "Plays over the last part of the next song; the song dips under it."
            : "Plays over the next song long enough to hold it; the song dips under it."}</p>
        </fieldset>
        {error && <div className="nv-warn bad">{error}</div>}
      </div>
    </Modal>
  );
}

function ComboEditor({ page, initial, onClose, onSaved }: { page: Page; initial: Combo; onClose: () => void; onSaved: (p: Page) => void }) {
  const [c, setC] = useState<Combo>(initial);
  const [error, setError] = useState<string | null>(null);
  const setItem = (i: number, patch: Partial<Combo["items"][number]>) => setC({ ...c, items: c.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const save = async () => {
    const out = await call<Page & { error?: string }>("combos/save", c);
    if (out.error) setError(out.error);
    else {
      toast(c.id ? "Combo saved." : "Combo added.", "ok");
      onSaved(out);
    }
  };
  return (
    <Modal title={c.id ? "Edit combo" : "New combo"} onClose={onClose}
      footer={<><span className="nv-spacer" /><button className="nv-btn" onClick={onClose}>Cancel</button><button className="nv-btn primary" onClick={() => void save()}>{c.id ? "Save" : "Add combo"}</button></>}>
      <div className="cl-form">
        <label className="nv-field"><span>Name</span><input className="nv-input" value={c.name} maxLength={60} autoFocus onChange={(e) => setC({ ...c, name: e.target.value })} placeholder="Hour opener" /></label>
        <ol className="cl-combo-items">
          {c.items.map((it, i) => (
            <li key={i}>
              <span className="cl-order">{i + 1}</span>
              <select className="nv-input" value={it.kind} onChange={(e) => setItem(i, { kind: e.target.value as "type" | "clip", ref: e.target.value === "type" ? page.types[0]?.id ?? "" : page.clips[0]?.trackId ?? "" })} aria-label="Kind">
                <option value="type">Next of a type</option>
                <option value="clip">One clip</option>
              </select>
              {it.kind === "type" ? (
                <select className="nv-input" value={it.ref} onChange={(e) => setItem(i, { ref: e.target.value })} aria-label="Type">
                  {page.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              ) : (
                <select className="nv-input" value={it.ref} onChange={(e) => setItem(i, { ref: e.target.value })} aria-label="Clip">
                  {page.clips.map((x) => <option key={x.trackId} value={x.trackId}>{x.title}</option>)}
                </select>
              )}
              <button className="nv-iconbtn" title="Move up" disabled={i === 0} onClick={() => setC({ ...c, items: moved(c.items, i, i - 1) })}><Icon name="up" size={14} /></button>
              <button className="nv-iconbtn" title="Move down" disabled={i === c.items.length - 1} onClick={() => setC({ ...c, items: moved(c.items, i, i + 1) })}><Icon name="down" size={14} /></button>
              <button className="nv-iconbtn" title="Remove" disabled={c.items.length === 1} onClick={() => setC({ ...c, items: c.items.filter((_, j) => j !== i) })}><Icon name="close" size={14} /></button>
            </li>
          ))}
        </ol>
        <button className="nv-btn" disabled={c.items.length >= 10} onClick={() => setC({ ...c, items: [...c.items, { kind: "type", ref: page.types[0]?.id ?? "" }] })}><Icon name="plus" /> Add a clip</button>
        {error && <div className="nv-warn bad">{error}</div>}
      </div>
    </Modal>
  );
}

function PreviewPane({ page }: { page: Page }) {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const [date, setDate] = useState(`${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`);
  const [minutes, setMinutes] = useState(3.5);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const signature = JSON.stringify(page.rules) + JSON.stringify(page.combos);

  useEffect(() => {
    let live = true;
    setBusy(true);
    const timer = window.setTimeout(() => {
      void call<Preview>("preview", { date, songMinutes: minutes }).then((p) => live && setPreview(p)).finally(() => live && setBusy(false));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [date, minutes, signature]);

  const byHour = useMemo(() => {
    const out = new Map<string, Preview["breaks"]>();
    for (const b of preview?.breaks ?? []) {
      const h = b.at.slice(11, 13);
      out.set(h, [...(out.get(h) ?? []), b]);
    }
    return [...out.entries()];
  }, [preview]);

  return (
    <aside className="cl-preview" aria-label="Preview a day">
      <header>
        <h3>Preview a day</h3>
        <div className="cl-row">
          <input className="nv-input" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} aria-label="Day" />
          <label className="cl-songlen">songs of <input className="nv-input cl-num" type="number" min={1} max={15} step={0.5} value={minutes} onChange={(e) => setMinutes(Number(e.target.value) || 3.5)} /> min</label>
        </div>
      </header>
      {preview?.error && <div className="nv-warn bad">{preview.error}</div>}
      {preview && !preview.error && (
        <>
          <ul className="cl-totals">
            {page.rules.filter((r) => r.enabled).map((r) => {
              const s = sentence(r, page);
              return <li key={r.id}><b>{preview.totals[String(r.id)] ?? 0}×</b> {s.what}, {s.when}, {s.often}</li>;
            })}
            {page.rules.every((r) => !r.enabled) && <li className="nv-dim">No rules are on.</li>}
          </ul>
          <p className="nv-dim">{preview.songs} songs of {minutes} min, with the clips the rules put between them{busy ? " · updating…" : ""}.</p>
          <div className="cl-timeline">
            {byHour.map(([hour, breaks]) => (
              <section key={hour}>
                <h4>{hour}:00</h4>
                {breaks.map((b) => (
                  <div key={b.at} className="cl-break">
                    <span className="cl-break-time">{b.at.slice(11, 16)}</span>
                    <span className="cl-break-clips">
                      {b.hits.flatMap((h) => h.clips.map((c) => ({ c, over: h.placement && h.placement !== "between" }))).map(({ c, over }, i) => (
                        <span key={i} className={`cl-chip ${over ? "over" : ""}`} title={over ? `${c.title ?? ""} (over the next song)` : c.title ?? ""}><em>{c.typeName}</em> {c.title}{over && <small> over song</small>}</span>
                      ))}
                    </span>
                    {b.show && <span className="nv-dim cl-break-show">{b.show}</span>}
                  </div>
                ))}
              </section>
            ))}
            {!byHour.length && <p className="nv-dim">No clips would play on this day.</p>}
          </div>
        </>
      )}
    </aside>
  );
}
